import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Drawer, Form, Input, InputNumber, Select, Space, Tag, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import { toast } from "react-toastify";
import { request } from "../../../functions/request";

const { Text, Paragraph } = Typography;

/**
 * Monta o marcador que a automação vai resolver na hora de publicar.
 *
 * Ninguém precisa decorar a sintaxe: escolhe o tipo, a commodity e a
 * praça numa lista que vem do próprio catálogo do banco, e o token sai
 * pronto para ser colado no modelo, no título ou no lead.
 */

// Cada tipo declara o que precisa perguntar. Os grupos seguem o jeito
// como as matérias são escritas: valores, a leitura deles (variação,
// sentido), as tabelas e o ranking das ações.
const TIPOS = [
  // ---- valores
  { grupo: "Valores", valor: "cotacao", rotulo: "Valor da cotação",
    ajuda: "O preço mais recente. Ex.: 370,65", commodity: true },
  { grupo: "Valores", valor: "valor_em", rotulo: "Valor numa data de referência",
    ajuda: "Ex.: o preço de 4 anos atrás — \"R$ 285,75\".", commodity: true, base: true },
  { grupo: "Valores", valor: "diferenca", rotulo: "Diferença em valor",
    ajuda: "Quanto mudou em reais, sem sinal. Ex.: \"280 reais abaixo\".", commodity: true, base: true },

  // ---- leitura do número
  { grupo: "Variação e indicadores", valor: "variacao", rotulo: "Variação (%)",
    ajuda: "Diferença percentual contra a base escolhida. Ex.: +2,1%", commodity: true, base: true, sinal: true },
  { grupo: "Variação e indicadores", valor: "sentido", rotulo: "Palavra pelo sinal (alta / queda)",
    ajuda: "Escolhe o texto conforme o preço subiu, caiu ou ficou parado. É o que deixa o parágrafo servir para qualquer dia.",
    commodity: true, base: true, palavras: true },
  { grupo: "Variação e indicadores", valor: "data_em", rotulo: "Data de referência",
    ajuda: "A data usada na comparação. Ex.: \"29 de setembro de 2022\".", commodity: true, base: true, formatoData: true },

  // ---- tabelas
  { grupo: "Tabelas", valor: "tabela_pracas", rotulo: "Tabela por praça",
    ajuda: "Uma linha por praça, com valor e variação.", commodity: true, pracasMultiplas: true, titulo: true },
  { grupo: "Tabelas", valor: "tabela_desempenho", rotulo: "Quadro de desempenho (vários períodos)",
    ajuda: "O quadro das matérias de Agroações: dia, acumulado no mês e no ano, 6, 12 e 36 meses. Serve para qualquer mercado.",
    commodity: true, periodos: true, titulo: true },
  { grupo: "Tabelas", valor: "ranking_tabela", rotulo: "Ranking de ações (tabela)",
    ajuda: "As maiores altas ou quedas das agroações no último pregão.", ranking: true, titulo: true },
  { grupo: "Tabelas", valor: "tabela_bolsa", rotulo: "Tabela de vencimentos (bolsa)",
    ajuda: "A curva do último pregão de uma bolsa.", bolsa: true, vencimentos: true, titulo: true },

  // ---- matérias de pregão (curva lançada em Fechamento de Bolsas > Lançar curva)
  { grupo: "Bolsas (matéria de pregão)", valor: "quadro_bolsa", rotulo: "Quadro de preços + conversão",
    ajuda: "As tabelas da matéria de bolsa: contratos, sessão de hoje, anterior, Var. e %, e a conversão de unidade.",
    bolsa: true, quadroBolsa: true, vencimentos: true, liquido: true, apos: true },
  { grupo: "Bolsas (matéria de pregão)", valor: "bolsa_campo", rotulo: "Dado de um contrato",
    ajuda: "Preço, preço convertido ou variação de um contrato. Ex.: \"opera negociado a US$ 353,60/ton curta\".",
    bolsa: true, quadroBolsa: true, contrato: true },
  { grupo: "Bolsas (matéria de pregão)", valor: "bolsa_sentido", rotulo: "Tom do pregão (positivos / negativos / mistos)",
    ajuda: "Pelo sinal de todos os contratos do quadro. Serve também para o lead (\"queda para a maioria dos contratos\").",
    bolsa: true, quadroBolsa: true, tomPregao: true },
  { grupo: "Bolsas (matéria de pregão)", valor: "bolsa_demais", rotulo: "Lista dos demais contratos",
    ajuda: "Uma linha por contrato, sem o de maior liquidez, com os verbos alternados como na matéria escrita à mão.",
    bolsa: true, quadroBolsa: true, liquido: true, demais: true },

  // ---- gráfico
  { grupo: "Gráficos", valor: "grafico_interativo", rotulo: "Gráfico interativo (Central de Gráficos)",
    ajuda: "O gráfico \"passe o mouse\", com o período contado a partir do dia da matéria — sem trocar datas à mão.",
    commodity: true, grafico: true },

  // ---- ranking em texto corrido
  { grupo: "Ações em texto corrido", valor: "ranking_campo", rotulo: "Um dado do ranking de ações",
    ajuda: "Nome, código, valor ou variação da 1ª, 2ª... posição. Para escrever \"A maior baixa foi da ...\".",
    ranking: true, posicao: true },

  // ---- datas e dólar
  { grupo: "Datas e dólar", valor: "dolar", rotulo: "Dólar comercial", ajuda: "Cotação do dólar comercial." },
  { grupo: "Datas e dólar", valor: "ptax", rotulo: "Dólar PTAX", ajuda: "Cotação PTAX do Banco Central." },
  { grupo: "Datas e dólar", valor: "data", rotulo: "Data de hoje", ajuda: "A data em que a matéria for gerada.", formatoHoje: true },
  { grupo: "Datas e dólar", valor: "dia_semana", rotulo: "Dia da semana", ajuda: "terça-feira" },
];

const BASES = [
  { value: "dia", label: "Pregão anterior (no dia)" },
  { value: "7d", label: "7 dias atrás" },
  { value: "30d", label: "30 dias atrás" },
  { value: "mes_atual", label: "Fim do mês passado (acumulado no mês)" },
  { value: "ano_atual", label: "Fim do ano passado (acumulado no ano)" },
  { value: "180d", label: "180 dias atrás (6 meses)" },
  { value: "ano", label: "Mesma data do ano passado" },
  { value: "3a", label: "Mesma data, 3 anos atrás" },
  { value: "4a", label: "Mesma data, 4 anos atrás" },
  { value: "5a", label: "Mesma data, 5 anos atrás" },
];

// Ações entram no catálogo pelo quadro de desempenho das matérias de
// Agroações; o prefixo evita confundir uma empresa com um mercado físico.
const rotuloDoMercado = (c) => `${c.tipo === 1 ? "Ação — " : ""}${String(c.nome).trim()}`;

const PERIODOS_PADRAO = ["dia", "mes_atual", "ano_atual", "180d", "ano", "3a"];

export default function PaletaTokens({ aberta, onFechar, onInserir }) {
  const [catalogo, setCatalogo] = useState({ commodities: [], bolsas: [] });
  const [tipo, setTipo] = useState("cotacao");
  const [commodity, setCommodity] = useState(null);
  const [mediaCom, setMediaCom] = useState(null);
  const [bolsa, setBolsa] = useState(null);
  const [uf, setUf] = useState(null);
  const [pracas, setPracas] = useState([]);
  const [base, setBase] = useState("dia");
  const [semSinal, setSemSinal] = useState(false);
  const [palavras, setPalavras] = useState({ alta: "alta", queda: "queda", estavel: "estabilidade" });
  const [formatoData, setFormatoData] = useState("extenso");
  const [periodos, setPeriodos] = useState(PERIODOS_PADRAO);
  const [ordem, setOrdem] = useState("quedas");
  const [quantos, setQuantos] = useState(6);
  const [excluir, setExcluir] = useState([]);
  const [posicao, setPosicao] = useState(1);
  const [campo, setCampo] = useState("nome");
  const [casas, setCasas] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [vencimentos, setVencimentos] = useState(4);
  const [periodoGrafico, setPeriodoGrafico] = useState("1a");
  const [formatoHoje, setFormatoHoje] = useState("curta");
  const [liquido, setLiquido] = useState("");
  const [apos, setApos] = useState("");
  const [contratoBolsa, setContratoBolsa] = useState("");
  const [campoBolsa, setCampoBolsa] = useState("valor");
  const [nDemais, setNDemais] = useState(3);
  const [tom, setTom] = useState({
    positivo: "positivos", negativo: "negativos", misto: "mistos", maioria_alta: "mistos", maioria_queda: "mistos",
  });

  useEffect(() => {
    if (!aberta || catalogo.commodities.length) return;
    request("/automacao/catalogo", { method: "GET" })
      .then(setCatalogo)
      .catch((erro) => {
        console.log(erro);
        toast.error("Não foi possível carregar a lista de mercados.");
      });
  }, [aberta, catalogo.commodities.length]);

  const d = TIPOS.find((t) => t.valor === tipo) || TIPOS[0];
  const escolhida = catalogo.commodities.find((c) => c.id === commodity);

  // O quadro da matéria de pregão mostra 6 contratos; a tabela simples, 4.
  useEffect(() => {
    setVencimentos(tipo === "quadro_bolsa" ? 6 : 4);
  }, [tipo]);

  // Troca de commodity limpa o que dependia dela.
  useEffect(() => {
    setUf(null);
    setPracas([]);
    setMediaCom(null);
  }, [commodity]);

  // A média só faz sentido com uma série da mesma tabela (a máxima com a
  // mínima do mesmo mercado) — o servidor recusa o resto, então a lista
  // já oferece só as compatíveis.
  const parceirasDeMedia = useMemo(
    () =>
      escolhida
        ? catalogo.commodities.filter((c) => c.id !== escolhida.id && (d.grafico || c.tabela === escolhida.tabela))
        : [],
    [escolhida, catalogo.commodities, d.grafico]
  );

  const token = useMemo(() => {
    const partes = [];
    if (d.grafico) {
      if (!commodity) return "";
      partes.push(`${tipo}:${[commodity, mediaCom].filter(Boolean).join(",")}`);
    } else if (d.commodity) {
      if (!commodity) return "";
      partes.push(`${tipo}:${commodity}`);
    } else if (d.bolsa) {
      if (!bolsa) return "";
      if (d.contrato && !contratoBolsa.trim()) return "";
      partes.push(`${tipo === "bolsa_campo" ? "bolsa" : tipo}:${bolsa}`);
    } else if (tipo === "ranking_tabela") {
      partes.push("ranking_acoes");
    } else if (tipo === "ranking_campo") {
      partes.push(`ranking_acoes:${posicao || 1}`);
    } else {
      partes.push(tipo);
    }

    if (d.commodity && !d.grafico && mediaCom) partes.push(`media_com=${mediaCom}`);
    if (d.formatoHoje && formatoHoje !== "curta") partes.push(`formato=${formatoHoje}`);
    if (d.pracasMultiplas && pracas.length) partes.push(`ufs=${pracas.join(",")}`);
    else if (d.commodity && uf) partes.push(`uf=${uf}`);
    if (d.grafico) partes.push(`periodo=${periodoGrafico}`);
    if (d.base && base !== "dia") partes.push(`base=${base}`);
    if (d.sinal && semSinal) partes.push("sinal=nao");
    if (d.palavras) {
      partes.push(`alta=${palavras.alta}`, `queda=${palavras.queda}`, `estavel=${palavras.estavel}`);
    }
    if (d.formatoData && formatoData !== "extenso") partes.push(`formato=${formatoData}`);
    if (d.periodos && periodos.length) partes.push(`periodos=${periodos.join(",")}`);
    if (d.ranking) {
      partes.push(`ordem=${ordem}`);
      if (tipo === "ranking_tabela") partes.push(`n=${quantos || 6}`);
      if (excluir.length) partes.push(`excluir=${excluir.join(",")}`);
    }
    if (d.posicao) {
      partes.push(`campo=${campo}`);
      if (campo === "variacao" && semSinal) partes.push("sinal=nao");
    }
    if (d.vencimentos) partes.push(`vencimentos=${vencimentos}`);
    if (d.liquido && liquido.trim()) partes.push(`liquido=${liquido.trim()}`);
    if (d.apos && apos) partes.push(`apos=${apos}`);
    if (d.demais) partes.push(`n=${nDemais || 3}`);
    if (d.contrato) {
      partes.push(`contrato=${contratoBolsa.trim()}`, `campo=${campoBolsa}`);
      if (campoBolsa === "variacao" && semSinal) partes.push("sinal=nao");
      if (campoBolsa === "sentido") {
        partes.push(`alta=${palavras.alta}`, `queda=${palavras.queda}`, `estavel=${palavras.estavel}`);
      }
    }
    if (d.tomPregao) {
      const PADRAO = { positivo: "positivos", negativo: "negativos", misto: "mistos", maioria_alta: "mistos", maioria_queda: "mistos" };
      Object.entries(tom).forEach(([k, v]) => { if (v !== PADRAO[k]) partes.push(`${k}=${v}`); });
    }
    if (casas !== null && casas !== undefined) partes.push(`casas=${casas}`);
    if (d.titulo && titulo.trim()) partes.push(`titulo=${titulo.trim()}`);

    return `{{${partes.join("|")}}}`;
  }, [tipo, d, commodity, mediaCom, bolsa, uf, pracas, base, semSinal, palavras, formatoData,
    periodos, ordem, quantos, excluir, posicao, campo, casas, titulo, vencimentos, periodoGrafico, formatoHoje,
    liquido, apos, contratoBolsa, campoBolsa, nDemais, tom]);

  const inserir = () => {
    if (!token) {
      toast.error("Escolha o mercado antes de inserir.");
      return;
    }
    onInserir(token);
    onFechar();
  };

  const grupos = [...new Set(TIPOS.map((t) => t.grupo))].map((g) => ({
    label: g,
    options: TIPOS.filter((t) => t.grupo === g).map((t) => ({ value: t.valor, label: t.rotulo })),
  }));

  return (
    <Drawer
      title="Inserir valor de mercado"
      open={aberta}
      onClose={onFechar}
      width={560}
      extra={
        <Button type="primary" onClick={inserir} disabled={!token}>
          Inserir
        </Button>
      }
    >
      <Form layout="vertical">
        <Form.Item label="O que inserir" extra={d.ajuda}>
          <Select showSearch optionFilterProp="label" value={tipo} onChange={setTipo} options={grupos} />
        </Form.Item>

        {d.commodity && (
          <Form.Item label="Mercado">
            <Select
              showSearch
              placeholder="Escolha a commodity"
              value={commodity}
              onChange={setCommodity}
              optionFilterProp="label"
              options={catalogo.commodities.map((c) => ({ value: c.id, label: rotuloDoMercado(c) }))}
            />
          </Form.Item>
        )}

        {d.commodity && parceirasDeMedia.length > 0 && (
          <Form.Item
            label={d.grafico ? "Segunda série no gráfico" : "Fazer média com"}
            extra={d.grafico
              ? "Opcional. O Indicador Café mostra a máxima e a mínima juntas."
              : "Opcional. Os indicadores IPAR são a média entre a máxima e a mínima do mesmo mercado."}
          >
            <Select
              allowClear
              showSearch
              placeholder={d.grafico ? "Só uma série" : "Sem média"}
              value={mediaCom}
              onChange={setMediaCom}
              optionFilterProp="label"
              options={parceirasDeMedia.map((c) => ({ value: c.id, label: rotuloDoMercado(c) }))}
            />
          </Form.Item>
        )}

        {d.bolsa && (
          <>
            <Form.Item label="Bolsa">
              <Select
                showSearch
                placeholder="Escolha a bolsa"
                value={bolsa}
                onChange={setBolsa}
                optionFilterProp="label"
                options={catalogo.bolsas
                  .filter((b) => !d.quadroBolsa || b.quadro)
                  .map((b) => ({ value: b.apelido, label: `${b.rotulo} (${b.unidade})` }))}
              />
            </Form.Item>
            {d.vencimentos && (
              <Form.Item label="Quantos vencimentos">
                <InputNumber min={1} max={12} value={vencimentos} onChange={(v) => setVencimentos(v || 4)} />
              </Form.Item>
            )}
          </>
        )}

        {d.liquido && (
          <Form.Item
            label="Contrato de maior liquidez"
            extra="Como aparece na matéria (ex.: dez/26). Na troca de contrato, é só atualizar aqui no modelo."
          >
            <Input value={liquido} onChange={(e) => setLiquido(e.target.value.replace(/[|{}]/g, ""))} placeholder="dez/26" />
          </Form.Item>
        )}

        {d.apos && (
          <Form.Item
            label="Só publicar se a curva foi lançada depois de"
            extra="Para as matérias do meio do pregão: sem isso, sairiam com a curva da abertura. Vazio para o fechamento."
          >
            <Input value={apos} onChange={(e) => setApos(e.target.value.replace(/[^\d:]/g, "").slice(0, 5))} placeholder="12:30" style={{ width: 120 }} />
          </Form.Item>
        )}

        {d.demais && (
          <Form.Item label="Quantos contratos na lista">
            <InputNumber min={1} max={10} value={nDemais} onChange={(v) => setNDemais(v || 3)} />
          </Form.Item>
        )}

        {d.contrato && (
          <Space size={12} style={{ display: "flex" }}>
            <Form.Item label="Contrato">
              <Input value={contratoBolsa} onChange={(e) => setContratoBolsa(e.target.value.replace(/[|{}]/g, ""))} placeholder="dez/26" style={{ width: 110 }} />
            </Form.Item>
            <Form.Item label="Dado" style={{ flex: 1 }}>
              <Select
                value={campoBolsa}
                onChange={setCampoBolsa}
                options={[
                  { value: "valor", label: "Preço de hoje" },
                  { value: "convertido", label: "Preço convertido (ex.: US$/ton métrica)" },
                  { value: "anterior", label: "Preço da sessão anterior" },
                  { value: "variacao", label: "Variação (%)" },
                  { value: "sentido", label: "Palavra pelo sinal (alta / baixa)" },
                ]}
              />
            </Form.Item>
          </Space>
        )}
        {d.contrato && campoBolsa === "variacao" && (
          <Form.Item>
            <Checkbox checked={semSinal} onChange={(e) => setSemSinal(e.target.checked)}>Sem sinal</Checkbox>
          </Form.Item>
        )}
        {d.contrato && campoBolsa === "sentido" && (
          <Form.Item label="Texto para cada caso">
            <Space direction="vertical" style={{ width: "100%" }}>
              {[["alta", "Se subiu"], ["queda", "Se caiu"], ["estavel", "Se ficou parado"]].map(([chave, r]) => (
                <Input
                  key={chave}
                  addonBefore={r}
                  value={palavras[chave]}
                  onChange={(e) => setPalavras((p) => ({ ...p, [chave]: e.target.value.replace(/[|{}]/g, "") }))}
                />
              ))}
            </Space>
          </Form.Item>
        )}

        {d.tomPregao && (
          <Form.Item label="Texto para cada caso">
            <Space direction="vertical" style={{ width: "100%" }}>
              {[
                ["positivo", "Todos em alta"], ["negativo", "Todos em queda"],
                ["maioria_alta", "Maioria em alta"], ["maioria_queda", "Maioria em queda"], ["misto", "Empate"],
              ].map(([chave, r]) => (
                <Input
                  key={chave}
                  addonBefore={r}
                  value={tom[chave]}
                  onChange={(e) => setTom((t) => ({ ...t, [chave]: e.target.value.replace(/[|{}]/g, "") }))}
                />
              ))}
            </Space>
          </Form.Item>
        )}

        {d.pracasMultiplas && escolhida?.pracas?.length > 0 && (
          <Form.Item label="Praças da tabela" extra="A ordem escolhida aqui é a ordem das linhas na tabela.">
            <Select
              mode="multiple"
              placeholder="Todas as praças com cotação"
              value={pracas}
              onChange={setPracas}
              options={escolhida.pracas.map((p) => ({ value: p, label: p }))}
            />
          </Form.Item>
        )}

        {d.commodity && !d.pracasMultiplas && escolhida?.pracas?.length > 0 && (
          <Form.Item label="Praça">
            <Select
              allowClear
              showSearch
              placeholder="Escolha o estado"
              optionFilterProp="label"
              value={uf}
              onChange={setUf}
              options={[...escolhida.pracas]
                .sort((a, b) => (a === "BR" ? -1 : b === "BR" ? 1 : 0))
                .map((p) => ({ value: p, label: p === "BR" ? "BR (média nacional)" : p }))}
            />
          </Form.Item>
        )}

        {d.base && (
          <Form.Item
            label="Comparar com"
            extra={base === "5a" || base === "4a"
              ? "Atenção: as matérias da casa chamam de \"cinco anos\" a comparação com a mesma data 4 anos atrás."
              : null}
          >
            <Select showSearch optionFilterProp="label" value={base} onChange={setBase} options={BASES} />
          </Form.Item>
        )}

        {d.grafico && (
          <Form.Item label="Período do gráfico" extra="Contado para trás a partir do dia em que a matéria sai.">
            <Select
              value={periodoGrafico}
              onChange={setPeriodoGrafico}
              options={[
                { value: "30d", label: "30 dias" },
                { value: "6m", label: "6 meses" },
                { value: "1a", label: "1 ano" },
                { value: "2a", label: "2 anos" },
                { value: "5a", label: "5 anos" },
              ]}
            />
          </Form.Item>
        )}

        {d.formatoHoje && (
          <Form.Item label="Formato">
            <Select
              value={formatoHoje}
              onChange={setFormatoHoje}
              options={[
                { value: "curta", label: "30/09/2026" },
                { value: "abreviada", label: "30 set 2026 (abertura da matéria)" },
                { value: "dia_mes", label: "30 de setembro" },
                { value: "extenso", label: "30 de setembro de 2026" },
              ]}
            />
          </Form.Item>
        )}

        {d.periodos && (
          <Form.Item label="Períodos do quadro" extra="Cada período vira uma linha, na ordem escolhida.">
            <Select mode="multiple" optionFilterProp="label" value={periodos} onChange={setPeriodos} options={BASES} />
          </Form.Item>
        )}

        {d.sinal && (
          <Form.Item>
            <Checkbox checked={semSinal} onChange={(e) => setSemSinal(e.target.checked)}>
              Sem sinal (para texto que já diz "alta de" ou "queda de")
            </Checkbox>
          </Form.Item>
        )}

        {d.palavras && (
          <Form.Item label="Texto para cada caso">
            <Space direction="vertical" style={{ width: "100%" }}>
              {[["alta", "Se subiu"], ["queda", "Se caiu"], ["estavel", "Se ficou parado"]].map(([chave, rotulo]) => (
                <Input
                  key={chave}
                  addonBefore={rotulo}
                  value={palavras[chave]}
                  onChange={(e) => setPalavras((p) => ({ ...p, [chave]: e.target.value.replace(/[|{}]/g, "") }))}
                />
              ))}
            </Space>
          </Form.Item>
        )}

        {d.formatoData && (
          <Form.Item label="Formato da data">
            <Select
              value={formatoData}
              onChange={setFormatoData}
              options={[
                { value: "extenso", label: "Por extenso (29 de setembro de 2022)" },
                { value: "curta", label: "Curta (29/09/2022)" },
              ]}
            />
          </Form.Item>
        )}

        {d.ranking && (
          <>
            <Form.Item label="Ranking de">
              <Select
                value={ordem}
                onChange={setOrdem}
                options={[
                  { value: "quedas", label: "Maiores quedas" },
                  { value: "altas", label: "Maiores altas" },
                ]}
              />
            </Form.Item>
            {tipo === "ranking_tabela" && (
              <Form.Item label="Quantas ações">
                <InputNumber min={1} max={20} value={quantos} onChange={(v) => setQuantos(v || 6)} />
              </Form.Item>
            )}
            <Form.Item
              label="Deixar de fora"
              extra="Códigos que não entram na lista (ex.: papel que não é do agro)."
            >
              <Select
                mode="tags"
                placeholder="ROMI3"
                value={excluir}
                onChange={(lista) => setExcluir(lista.map((c) => c.trim().toUpperCase()).filter(Boolean))}
              />
            </Form.Item>
          </>
        )}

        {d.posicao && (
          <Space size={12} style={{ display: "flex" }}>
            <Form.Item label="Posição">
              <InputNumber min={1} max={50} value={posicao} onChange={(v) => setPosicao(v || 1)} />
            </Form.Item>
            <Form.Item label="Dado" style={{ flex: 1 }}>
              <Select
                value={campo}
                onChange={setCampo}
                options={[
                  { value: "nome", label: "Nome da empresa" },
                  { value: "codigo", label: "Código (SMTO3)" },
                  { value: "valor", label: "Valor atual" },
                  { value: "anterior", label: "Valor anterior" },
                  { value: "variacao", label: "Variação (%)" },
                ]}
              />
            </Form.Item>
          </Space>
        )}
        {d.posicao && campo === "variacao" && (
          <Form.Item>
            <Checkbox checked={semSinal} onChange={(e) => setSemSinal(e.target.checked)}>
              Sem sinal
            </Checkbox>
          </Form.Item>
        )}

        {d.titulo && (
          <Form.Item label="Título da tabela" extra="Opcional. Aceita outros marcadores dentro.">
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Soja em {{data}}" />
          </Form.Item>
        )}

        {(d.commodity || d.bolsa) && !d.grafico && !d.tomPregao && tipo !== "sentido" && tipo !== "data_em" && (
          <Form.Item label="Casas decimais" extra="Vazio usa o padrão do mercado.">
            <InputNumber min={0} max={4} value={casas} onChange={setCasas} placeholder="padrão" />
          </Form.Item>
        )}

        <Content style={{ background: "#fafafa", padding: 12, borderRadius: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Marcador que será inserido:
          </Text>
          <Paragraph copyable={token ? { text: token } : false} style={{ marginBottom: 0, marginTop: 6 }}>
            <Tag color={token ? "blue" : "default"} style={{ fontFamily: "monospace", whiteSpace: "normal" }}>
              {token || "escolha o mercado"}
            </Tag>
          </Paragraph>
          <Space direction="vertical" size={2} style={{ marginTop: 10 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              O valor entra na matéria na hora da publicação, direto do banco.
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Se o mercado não tiver cotação do dia, a matéria não é publicada.
            </Text>
          </Space>
        </Content>
      </Form>
    </Drawer>
  );
}
